import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, setToken } from "../lib/api";
import { toast } from "sonner";
import { Lock, ArrowLeft, Loader2 } from "lucide-react";

export default function AdminLogin() {
    const [email, setEmail] = useState("admin@example.com");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const navigate = useNavigate();

    const submit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setSubmitting(true);
        try {
            const { data } = await api.post("/admin/login", { email, password });
            setToken(data.token);
            toast.success("Welcome back");
            navigate("/admin");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Login failed");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="min-h-screen flex items-center justify-center px-6 bg-medical-50 relative"
            data-testid="admin-login-page"
        >
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_15%,#CCFBF1_0%,transparent_45%),radial-gradient(circle_at_85%_85%,#F1F5F9_0%,transparent_45%)] opacity-70 pointer-events-none" />
            <div className="relative w-full max-w-md">
                <Link
                    to="/"
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-teal-700 mb-6"
                    data-testid="admin-login-back-link"
                >
                    <ArrowLeft className="h-3.5 w-3.5" />
                    Back to whiteboard
                </Link>
                <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
                    <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.25em] text-teal-700 mb-3">
                        <Lock className="h-3 w-3" />
                        Admin Access
                    </div>
                    <h1 className="font-display text-3xl font-black text-medical-900 mb-1 tracking-tight">
                        Sign in
                    </h1>
                    <p className="text-sm text-slate-500 mb-6">
                        Manage pages, ordering and the table of contents.
                    </p>

                    <form onSubmit={submit} className="space-y-4">
                        <div>
                            <label className="block text-xs font-semibold text-medical-800 uppercase tracking-wider mb-1.5">
                                Email
                            </label>
                            <input
                                data-testid="admin-login-email-input"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                className="w-full h-11 px-3 border border-slate-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-teal-600"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-medical-800 uppercase tracking-wider mb-1.5">
                                Password
                            </label>
                            <input
                                data-testid="admin-login-password-input"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                className="w-full h-11 px-3 border border-slate-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-teal-600"
                            />
                        </div>
                        <button
                            data-testid="admin-login-submit"
                            type="submit"
                            disabled={submitting}
                            className="w-full h-11 inline-flex items-center justify-center gap-2 bg-teal-700 hover:bg-teal-800 text-white rounded-md font-medium transition-colors disabled:opacity-60"
                        >
                            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                            Sign in
                        </button>
                    </form>
                    <p className="mt-6 text-[11px] font-mono text-slate-400">
                        Default credentials seeded on first run: admin@example.com / admin123
                    </p>
                </div>
            </div>
        </div>
    );
}
